import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Table, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash, FaEyeDropper, FaFileInvoiceDollar, FaArrowLeft, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';
import AuthContext from '../../context/AuthContext';
import { customerService } from '../../services/api';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [customer, setCustomer] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchCustomerData();
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      
      // Fetch customer details, prescriptions, and invoices in parallel
      const [customerRes, prescriptionsRes, invoicesRes] = await Promise.all([
        customerService.getCustomerById(id),
        customerService.getPrescriptionsByCustomer(id),
        customerService.getInvoicesByCustomer(id)
      ]);
      
      setCustomer(customerRes?.data.customer || []);
      // console.log("customerRes?.data",customerRes?.data)
      setPrescriptions(prescriptionsRes?.data?.prescriptions || []);
      // console.log("prescriptionsRes?.data?.prescriptions",prescriptionsRes?.data?.prescriptions)
      setInvoices(invoicesRes?.data?.invoices || []);
      // console.log("invoicesRes?.data?.invoices",invoicesRes?.data?.invoices)
      setLoading(false);
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setError('Failed to load customer data',err.response?.data?.msg);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await customerService.deleteCustomer(id);
      toast.success('Customer deleted successfully');
      navigate('/customers');
    } catch (err) {
      console.error('Error deleting customer:', err);
      toast.error(err.response?.data?.msg || 'Failed to delete customer');
    }
    setShowDeleteModal(false);
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
        <Button as={Link} to="/customers" variant="primary">
          <FaArrowLeft className="me-2" /> Back to Customers
        </Button>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container>
        <Alert variant="warning">Customer not found</Alert>
        <Button as={Link} to="/customers" variant="primary">
          <FaArrowLeft className="me-2" /> Back to Customers
        </Button>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
        {console.log(customer)}
          <h1>{customer.name}</h1>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/customers" variant="outline-secondary" className="me-2">
            <FaArrowLeft className="me-1" /> Back
          </Button>
          <Button as={Link} to={`/customers/edit/${id}`} variant="outline-primary" className="me-2">
            <FaEdit className="me-1" /> Edit
          </Button>
          <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>
            <FaTrash className="me-1" /> Delete
          </Button>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Contact Information</Card.Header>
            <Card.Body>
              <p><strong>Phone:</strong> {customer?.phone}</p>
              <p><strong>Email:</strong> {customer?.email || 'Not provided'}</p>
              <p>
                <strong>Address:</strong>{' '}
                {customer?.address && Object.values(customer.address).some(val => val) ? (
                  <>
                    {customer?.address.street && <>{customer.address.street}, <br /></>}
                    {customer?.address.city && <>{customer.address.city}, </>}
                    {customer?.address.state && <>{customer.address.state}, </>}
                    {customer?.address.zipCode && <>{customer.address.zipCode}, </>}
                    {customer?.address.country && <>{customer.address.country}</>}
                  </>
                ) : (
                  'Not provided'
                )}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>Eye Details</Card.Header>
            <Card.Body>
              {customer.eyeDetails && (
                Object.values(customer.eyeDetails.rightEye).some(val => val) || 
                Object.values(customer.eyeDetails.leftEye).some(val => val) || 
                customer.eyeDetails.notes
              ) ? (
                <>
                  <Row>
                    <Col md={6}>
                      <h5>Right Eye</h5>
                      <p><strong>Sphere:</strong> {customer.eyeDetails.rightEye.sphere || 'N/A'}</p>
                      <p><strong>Cylinder:</strong> {customer.eyeDetails.rightEye.cylinder || 'N/A'}</p>
                      <p><strong>Axis:</strong> {customer.eyeDetails.rightEye.axis || 'N/A'}</p>
                      <p><strong>Add:</strong> {customer.eyeDetails.rightEye.add || 'N/A'}</p>
                      <p><strong>PD:</strong> {customer.eyeDetails.rightEye.pd || 'N/A'}</p>
                    </Col>
                    <Col md={6}>
                      <h5>Left Eye</h5>
                      <p><strong>Sphere:</strong> {customer.eyeDetails.leftEye.sphere || 'N/A'}</p>
                      <p><strong>Cylinder:</strong> {customer.eyeDetails.leftEye.cylinder || 'N/A'}</p>
                      <p><strong>Axis:</strong> {customer.eyeDetails.leftEye.axis || 'N/A'}</p>
                      <p><strong>Add:</strong> {customer.eyeDetails.leftEye.add || 'N/A'}</p>
                      <p><strong>PD:</strong> {customer.eyeDetails.leftEye.pd || 'N/A'}</p>
                    </Col>
                  </Row>
                  
                  {customer.eyeDetails.notes && (
                    <>
                      <h5 className="mt-3">Notes</h5>
                      <p>{customer.eyeDetails.notes}</p>
                    </>
                  )}
                </>
              ) : (
                <p>No eye details provided</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>Prescriptions</div>
              <Button as={Link} to={`/prescriptions/new/${id}`} variant="primary" size="sm">
                <FaEyeDropper className="me-1" /> Add Prescription
              </Button>
            </Card.Header>
            <Card.Body>
              {prescriptions.length === 0 ? (
                <p>No prescriptions found for this customer.</p>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Doctor</th>
                        <th>Type</th>
                        <th>Expiry</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prescriptions.map((prescription) => (
                        <tr key={prescription._id}>
                          <td>{new Date(prescription.prescriptionDate).toLocaleDateString()}</td>
                          <td>{prescription.doctorName}</td>
                          <td>{prescription.prescriptionType}</td>
                          <td>
                            {prescription.expiryDate ? 
                              new Date(prescription.expiryDate).toLocaleDateString() : 
                              'N/A'}
                          </td>
                          <td className="text-center">
                            <Button
                              as={Link}
                              to={`/prescriptions/${prescription._id}`}
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              title="View Prescription"
                            >
                              <FaEye />
                            </Button>
                            <Button
                              as={Link}
                              to={`/invoices/new/${id}?prescription=${prescription._id}`}
                              variant="outline-info"
                              size="sm"
                              title="Create Invoice"
                            >
                              <FaFileInvoiceDollar />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>Invoices</div>
              <Button as={Link} to={`/invoices/new/${id}`} variant="primary" size="sm">
                <FaFileInvoiceDollar className="me-1" /> Create Invoice
              </Button>
            </Card.Header>
            <Card.Body>
              {invoices.length === 0 ? (
                <p>No invoices found for this customer.</p>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice._id}>
                          <td>{invoice.invoiceNumber}</td>
                          <td>{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                          <td>${invoice.total ? invoice.total.toFixed(2) : '0.00'}</td>
                          <td>
                            <span className={`badge bg-${
                              invoice.paymentStatus === 'paid' ? 'success' :
                              invoice.paymentStatus === 'partial' ? 'warning' : 'danger'
                            }`}>
                              {invoice.paymentStatus.charAt(0).toUpperCase() + invoice.paymentStatus.slice(1)}
                            </span>
                          </td>
                          <td className="text-center">
                            <Button
                              as={Link}
                              to={`/invoices/${invoice._id}`}
                              variant="outline-primary"
                              size="sm"
                              title="View Invoice"
                            >
                              <FaEye />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {customer.name}?</p>
          <p className="text-danger">
            This will also delete all associated prescriptions and invoices. This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomerDetail;
